import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from './Logo';

const serviceLinks = [
  { label: 'IT Managed Services', href: '/services#it-managed-services' },
  { label: 'Cybersecurity Solutions', href: '/services#cybersecurity-solutions' },
  { label: 'Cloud Computing', href: '/services#cloud-computing' },
  { label: 'IT Consulting', href: '/services#it-consulting' },
  { label: 'White-Label SaaS', href: '/services#custom-white-label-saas' },
  { label: 'Disaster Recovery', href: '/services#disaster-recovery' },
];

const companyLinks = [
  { label: 'About Us', href: '/about' },
  { label: 'Why Choose Us', href: '/why-choose-us' },
  { label: 'Insights & Blog', href: '/insights' },
  { label: 'Contact Us', href: '/contact' },
];

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleServiceLink = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const [path, hash] = href.split('#');
    if (window.location.pathname === path) {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate(path);
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    }
  };

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    setError('');
    try {
      const body = new URLSearchParams({ email });
      await fetch('https://readdy.ai/api/form/d6uptodt4s7j0tv5i820', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
      setEmail('');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-white border-t border-black/[0.06]">
      <div className="border-b border-black/[0.06]">
        <div className="mx-auto px-6 lg:px-10 xl:px-16 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#c97a2b]/10 flex-shrink-0">
                <i className="ri-mail-send-line text-[#c97a2b] text-base" />
              </div>
              <div>
                <p className="text-page-ink font-semibold text-sm">SunStone Newsletter</p>
                <p className="text-page-faint text-xs">IT insights. No spam. Unsubscribe anytime.</p>
              </div>
            </div>
            {submitted ? (
              <div className="flex items-center gap-2 text-page-muted text-sm">
                <i className="ri-checkbox-circle-line text-[#c97a2b]" />
                <span>Thank you for subscribing!</span>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} data-readdy-form id="footer-newsletter" className="flex gap-2 w-full sm:w-auto">
                <input
                  placeholder="Your email address"
                  required
                  className="flex-1 sm:w-60 px-4 py-2.5 rounded-md text-sm text-page-ink placeholder-page-faint bg-white border border-black/[0.08] focus:border-[#c97a2b]/50 focus:outline-none transition-colors"
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary text-sm px-5 py-2.5 disabled:opacity-60"
                >
                  {submitting ? '...' : 'Subscribe'}
                </button>
              </form>
            )}
            {error && <p className="text-red-500 text-xs">{error}</p>}
          </div>
        </div>
      </div>

      <div className="mx-auto px-6 lg:px-10 xl:px-16 pt-14 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 xl:gap-14 mb-12">
          <div>
            <div className="mb-5">
              <Logo />
            </div>
            <p className="text-page-muted text-[13px] leading-relaxed max-w-xs mb-6">
              Empowering businesses through innovative IT solutions, secure infrastructure, and strategic technology consulting since 2021.
            </p>
            <div className="flex items-center gap-2">
              {[
                { icon: 'linkedin', href: '#' },
                { icon: 'twitter-x', href: '#' },
                { icon: 'facebook', href: '#' },
                { icon: 'youtube', href: '#' },
              ].map(({ icon, href }) => (
                <a
                  key={icon}
                  href={href}
                  className="w-8 h-8 flex items-center justify-center rounded-md bg-black/[0.04] text-page-faint hover:bg-[#c97a2b]/15 hover:text-[#c97a2b] transition-all duration-200 cursor-pointer"
                >
                  <i className={`ri-${icon}-fill text-sm`} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-page-faint font-semibold text-[11px] uppercase tracking-[0.15em] mb-5">Services</h4>
            <ul className="space-y-2.5">
              {serviceLinks.map(({ label, href }) => (
                <li key={label}>
                  <a
                    href={href}
                    onClick={(e) => handleServiceLink(e, href)}
                    className="text-page-muted text-[13px] hover:text-page-ink transition-colors duration-200 cursor-pointer"
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-page-faint font-semibold text-[11px] uppercase tracking-[0.15em] mb-5">Company</h4>
            <ul className="space-y-2.5">
              {companyLinks.map(({ label, href }) => (
                <li key={label}>
                  <Link
                    to={href}
                    className="text-page-muted text-[13px] hover:text-page-ink transition-colors duration-200"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-page-faint font-semibold text-[11px] uppercase tracking-[0.15em] mb-5">Get In Touch</h4>
            <div className="space-y-3.5">
              <div className="flex items-start gap-3">
                <i className="ri-phone-line text-[#c97a2b] text-sm mt-0.5" />
                <span className="text-page-muted text-[13px]">+234 8033019908</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-mail-line text-[#c97a2b] text-sm mt-0.5" />
                <span className="text-page-muted text-[13px]">info@sunstonesystem.com</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-map-pin-line text-[#c97a2b] text-sm mt-0.5" />
                <span className="text-page-muted text-[13px]">
                  Suite 30, District Plaza, Simbiat Abiola Way, Ikeja, Lagos. Nigeria.
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-black/[0.06] pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-page-faint text-[12px]">
            &copy; 2026 SunStone Systems. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            {['Privacy Policy', 'Cookie Policy'].map((item) => (
              <a key={item} href="#" className="text-page-faint text-[12px] hover:text-page-ink transition-colors whitespace-nowrap">
                {item}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
